import React from 'react';
import styled from 'styled-components';
import { Heading4 } from '@latitude/heading';
import {
  COLOR,
  ICON_SIZE,
  FONT_WEIGHT,
  MARGIN,
  FONT_SIZE,
  BREAKPOINT,
  BORDER_RADIUS,
  PADDING
} from '@/utils/constants';
import Section from '@latitude/section';
import { Text } from '@latitude/text';
import InfoBox from '@/components/InfoBox/InfoBox';
import CustomAccordion from './CustomAccordion';
import SvgInline from '@latitude/svg-inline';
import { Button } from '@latitude/button';
import { StickyNavigation } from '@latitude/sticky-navigation';
import { Link } from '@latitude/link';
import { BUTTON_STYLE } from '@latitude/core/utils/constants';
import * as ProfileIcon from '@/components/Icon/svg/profile-1.svg';
import * as IdIcon from '@/components/Icon/svg/id.svg';
import * as FinanceIcon from '@/components/Icon/svg/finance.svg';
import * as CommsIcon from '@/components/Icon/svg/comms.svg';
import * as WalletIcon from '@/components/Icon/svg/wallet.svg';

// Each step with icon, heading and children
const StepItem = ({ id, icon, headingText, children }) => {
  const StyledOut = styled.div``;
  const StyledContainer = styled.div`
    display: flex;
    direction: row;
    align-items: start;
    justify-content: start;
    gap: 8px;
    margin: ${MARGIN.M16} 0;
  `;

  const StyledHeading = styled(Heading4)`
    margin: 0;
  `;

  const StyledDescription = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${MARGIN.M8};
  `;

  const StyledIcon = styled(icon)`
    width: ${ICON_SIZE.SM};
    height: ${ICON_SIZE.SM};
    margin-top: -2px;
    stroke: ${COLOR.BRAND_PRIMARY};
    fill: none;
    stroke-width: 2.5px;

    @media (min-width: ${BREAKPOINT.MD}) {
      margin-top: 2px;
    }
  `;
  return (
    <StyledOut id={id}>
      <StyledContainer>
        <StyledIcon />
        <StyledHeading color={COLOR.BRAND_PRIMARY}>{headingText}</StyledHeading>
      </StyledContainer>
      <StyledDescription>{children}</StyledDescription>
    </StyledOut>
  );
};

const CheckItem = ({ text }) => {
  const StyledCheckItem = styled.div`
    display: flex;
    direction: row;
    align-items: start;
  `;

  const StyledCheckText = styled(Text)`
    margin-top: ${MARGIN.M4};
  `;

  const StyledIcon = styled(SvgInline)`
    width: ${ICON_SIZE.SM};
    height: ${ICON_SIZE.SM};
  `;
  return (
    <StyledCheckItem>
      <StyledIcon name="tick-blue" strokeBlueBright />
      <StyledCheckText>{text}</StyledCheckText>
    </StyledCheckItem>
  );
};

const HowToApplySteps = ({ getStartedUrl, ctaID }) => {
  const BoldText = styled.span`
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  `;

  const StyledUL = styled.ul`
    margin-block-start: 0;
  `;

  const StyledLI = styled.li`
    font-size: ${FONT_SIZE.NORMAL};
    font-weight: ${FONT_WEIGHT.LIGHT};
  `;

  const StyledUploadDocsImage = styled.img`
    width: 100%;
    max-width: 500px;
    object-fit: cover;
    margin: ${MARGIN.M8} 0;
    box-shadow: 0 2px 8px ${COLOR.GREY10};
    padding: ${PADDING.P8};
    border-radius: ${BORDER_RADIUS.BRAND};
  `;

  const StyledUploadStep = styled.div`
    margin: ${MARGIN.M8} 0;
  `;

  const StyledHeading = styled(Heading4)`
    margin: ${MARGIN.M8} 0;
  `;

  const StyledButton = styled(Button)`
    margin: ${MARGIN.M8} 0;
  `;

  const DesktopOnly = styled.div`
    display: none;
    @media (min-width: ${BREAKPOINT.LG}) {
      display: block;
    }
  `;
  const MobileAndTabletOnly = styled.div`
    display: block;
    @media (min-width: ${BREAKPOINT.LG}) {
      display: none;
    }
  `;

  const StyledSection = styled(Section)`
    padding-top: 10px;
  `;

  const StyledLink = styled(Link)`
    width: 100%;
    margin-top: ${MARGIN.M4};
    @media (min-width: ${BREAKPOINT.LG}) {
      width: fit-content;
    }
  `;

  return (
    <StyledSection>
      <DesktopOnly>
        <StickyNavigation
          items={[
            {
              anchor: 'eligibility',
              label: 'Eligibility'
            },
            {
              anchor: 'identity',
              label: 'Identity'
            },
            {
              anchor: 'proof-of-income',
              label: 'Proof of income'
            },
            {
              anchor: 'expenses',
              label: 'Expenses'
            },
            {
              anchor: 'documentation',
              label: 'Documentation'
            }
          ]}
          enableAnchorUpdate={true}
          ctaHref={getStartedUrl}
          ctaText={'Get Started'}
          offsetElem="[data-sticky-navigation-offset]"
          trackId={`${ctaID}-sticky-nav`}
        />
      </DesktopOnly>
      <StepItem
        id="eligibility"
        icon={ProfileIcon}
        headingText={'Step 1: Check your eligibility'}
      >
        <BoldText>You must be</BoldText>
        <StyledUL>
          <StyledLI>18 years of age or older</StyledLI>
          <StyledLI>
            A resident of Australia or a temporary resident who legally has the
            right to work in Australia (and intends to stay) for at least the
            next 12 months
          </StyledLI>
        </StyledUL>
      </StepItem>

      <StepItem
        id="identity"
        icon={IdIcon}
        headingText={'Step 2: Prove your identity'}
      >
        <Text>
          To apply for this account, you'll need at least one form of ID so we
          can verify your identity. However, if possible, please try and provide
          two forms of ID.
        </Text>
        <CheckItem text={'Australian Passport'} />
        <CheckItem text={'Australian Driver Licence'} />
        <CheckItem text={'Medicare Card'} />
      </StepItem>

      <StepItem
        id="proof-of-income"
        icon={FinanceIcon}
        headingText={'Step 3: Show proof of income'}
      >
        <Text>
          Depending on what you select as your main source of income, we'll ask
          for documents to show it. We're trying to understand your current
          situation, so always upload the most recent version of your document.
        </Text>
        <Text>
          Your individual employment status will determine the documents you may
          need to provide us. All documents must contain your full legal name.
        </Text>
        <CustomAccordion />
      </StepItem>

      <StepItem
        id="expenses"
        icon={WalletIcon}
        headingText={'Step 4: Calculate your monthly expenses'}
      >
        <Text>
          When applying for a Latitude credit card, you will need to enter your
          share of monthly expenses. Below is a list of household and living
          expenses you should consider:
        </Text>

        <BoldText>Household expenses</BoldText>
        <StyledUL>
          <StyledLI>Home loan, rent or board.</StyledLI>
        </StyledUL>

        <BoldText>Living expenses</BoldText>
        <StyledUL>
          <StyledLI>
            Food, holidays, recreation and entertainment e.g. regular spend on
            food, household, supplies, domestic/international travel, hobbies,
            dining out and other leisure activities.
          </StyledLI>
          <StyledLI>
            Insurance e.g. home and contents, life, health, vehicle, total and
            permanent disability, additional superannuation contributions.{' '}
          </StyledLI>
          <StyledLI>
            Utilities and other housing costs e.g. Electricity, gas, water,
            telephone, internet, streaming subscriptions.
          </StyledLI>
          <StyledLI>
            Education and childcare e.g. public and private school fees,
            childcare costs and related expenses.
          </StyledLI>
          <StyledLI>
            Transport e.g. public transport, registration, servicing, petrol,
            parking fees and road tolls.
          </StyledLI>
          <StyledLI>
            Clothing, personal care, health and medical e.g. clothing, personal
            hygiene products, gym memberships, doctor visits or medications.
          </StyledLI>
          <StyledLI>
            Other financial commitments e.g. credit cards, personal loans or
            investment property loans.
          </StyledLI>
        </StyledUL>
      </StepItem>

      <StepItem
        id="documentation"
        icon={CommsIcon}
        headingText={'Step 5: Upload your documentation'}
      >
        <InfoBox>
          <Text>
            Check the maximum file size limit (e.g., 5MB per file, 22MB total
            upload). Make sure your documents are clear and readable.
          </Text>
        </InfoBox>

        {/* This will be visible on desktop only */}
        <DesktopOnly>
          <Text>
            <BoldText>
              If you are on a laptop or desktop follow these steps to upload
              your payslips or bank statements/other documents:
            </BoldText>
          </Text>
          <StyledUploadStep>
            <Text>
              1. Under the <BoldText>Verify your income</BoldText> section,
              choose <BoldText>Upload payslips</BoldText> or
              <BoldText> bank statements/other documents.</BoldText>
            </Text>
            <StyledUploadDocsImage
              alt="upload"
              src="/images/credit-cards/how-to-apply/desktop/upload.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>2. You can:</Text>
            <StyledUL>
              <StyledLI>
                <Text>
                  <BoldText>Drag and drop files</BoldText> into the uploader, or
                </Text>
                <StyledUploadDocsImage
                  alt="drop"
                  src="/images/credit-cards/how-to-apply/desktop/drop.webp"
                />
              </StyledLI>
              <StyledLI>
                <Text>
                  Select <BoldText>choose file</BoldText> to manually upload.
                </Text>
                <StyledUploadDocsImage
                  alt="choose"
                  src="/images/credit-cards/how-to-apply/desktop/choose.webp"
                />
              </StyledLI>
            </StyledUL>
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              3. Your documents have been uploaded successfully and should
              appear in the files attached section.
            </Text>
            <StyledUploadDocsImage
              alt="select"
              src="/images/credit-cards/how-to-apply/desktop/select.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              4. You have received an error. Try choosing another file or
              submitting the files you have uploaded already.
            </Text>
            <StyledUploadDocsImage
              alt="error"
              src="/images/credit-cards/how-to-apply/desktop/error.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              5. When you finish uploading your documents, click{' '}
              <BoldText>Submit</BoldText>. You'll see the upload progress and
              get confirmation once your documents are received.
            </Text>
            <StyledUploadDocsImage
              alt="submit"
              src="/images/credit-cards/how-to-apply/desktop/submit.webp"
            />
          </StyledUploadStep>

          <StyledHeading color={COLOR.BRAND_PRIMARY}>
            Ready to apply?
          </StyledHeading>
          <Text>
            If you've checked you're eligible to apply and have all your details
            and documents ready, let's get started.
          </Text>
          <StyledLink
            button={BUTTON_STYLE.PRIMARY}
            className="get-started-cta"
            href={getStartedUrl}
            trackId={`${ctaID}-get-started` || 'get-started-cta'}
          >
            Get Started
          </StyledLink>
        </DesktopOnly>

        {/* This will be visible on mobile and desktop only*/}
        <MobileAndTabletOnly>
          <Text>
            <BoldText>
              If you are on a mobile or tablet device follow these steps to
              upload your payslips or bank statements/other documents:
            </BoldText>
          </Text>
          <StyledUploadStep>
            <Text>
              1. Under the <BoldText>Verify your income</BoldText> section,
              choose <BoldText>Upload payslips</BoldText> or
              <BoldText> bank statements/other documents.</BoldText>
            </Text>
            <StyledUploadDocsImage
              alt="upload"
              src="/images/credit-cards/how-to-apply/mobile/upload.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              2. Tap <BoldText>choose file</BoldText> to upload your document.
            </Text>
            <StyledUploadDocsImage
              alt="choose"
              src="/images/credit-cards/how-to-apply/mobile/choose.webp"
            />
            <StyledUploadDocsImage
              alt="select"
              src="/images/credit-cards/how-to-apply/mobile/select.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              3. Your documents have been uploaded successfully and should
              appear in the files attached section.
            </Text>
            <StyledUploadDocsImage
              alt="attach"
              src="/images/credit-cards/how-to-apply/mobile/attach.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              4. You have received an error. Try choosing another file or
              submitting the files you have uploaded already.
            </Text>
            <StyledUploadDocsImage
              alt="error"
              src="/images/credit-cards/how-to-apply/mobile/error.webp"
            />
          </StyledUploadStep>

          <StyledUploadStep>
            <Text>
              5. When you finish uploading your documents, click{' '}
              <BoldText>Submit</BoldText>. You'll see the upload progress and
              get confirmation once your documents are received.
            </Text>
          </StyledUploadStep>

          <StyledHeading color={COLOR.BRAND_PRIMARY}>
            Ready to apply?
          </StyledHeading>
          <Text>
            If you've checked you're eligible to apply and have all your details
            and documents ready, let's get started.
          </Text>
          <StyledLink
            button={BUTTON_STYLE.PRIMARY}
            className="get-started-cta"
            href={getStartedUrl}
            trackId={`${ctaID}-get-started` || 'get-started-cta'}
          >
            Get Started
          </StyledLink>
        </MobileAndTabletOnly>
      </StepItem>
    </StyledSection>
  );
};

export default HowToApplySteps;
